<template>
  <v-container>
    <!-- Overlay loader -->
    <v-overlay v-model="overlay" color="white" opacity="1" persistent>
      <v-container style="text-align:center">
        <v-progress-circular :size="50" color="#234a98" indeterminate></v-progress-circular>
        <p class="pt-2" style="font-size:14px;color:#234a98">{{ $t('cleaningEfficiency.loading') }}</p>
      </v-container>
    </v-overlay>

    <!-- ── FILTER BAR (stile WashroomFrequencyV2) ── -->
    <v-card class="mb-4" width="100%" flat>
      <v-card-text>
        <v-layout class="mt-4" id="changeMyColor">
          <!-- Client -->
          <v-flex xs6 sm3 md2>
            <v-select
              v-model="client"
              :items="clients"
              :label="$t('cleaningEfficiency.client')"
              outlined
              prepend-inner-icon="mdi-domain"
              append-icon="mdi-arrow-down-drop-circle-outline"
              :no-data-text="$t('common.noDataAvailable')">
            </v-select>
          </v-flex>
          <!-- Building -->
          <v-flex xs6 sm3 md2>
            <v-select class="ml-2"
              v-model="building"
              :items="buildings"
              :label="$t('cleaningEfficiency.building')"
              outlined
              :key="'b'+bKey"
              :disabled="buildingActive"
              prepend-inner-icon="mdi-office-building-outline"
              append-icon="mdi-arrow-down-drop-circle-outline"
              :no-data-text="$t('common.noDataAvailable')">
            </v-select>
          </v-flex>
          <!-- Floor -->
          <v-flex xs6 sm3 md2>
            <v-select class="ml-2"
              v-model="floor"
              :items="floors"
              :label="$t('cleaningEfficiency.floor')"
              outlined
              :key="'f'+fKey"
              :disabled="floorActive"
              prepend-inner-icon="mdi-layers-outline"
              append-icon="mdi-arrow-down-drop-circle-outline"
              :no-data-text="$t('common.noDataAvailable')">
            </v-select>
          </v-flex>
          <!-- Area -->
          <v-flex xs6 sm3 md2>
            <v-select class="ml-2"
              v-model="area"
              :items="areas"
              :label="$t('cleaningEfficiency.area')"
              outlined
              :key="'a'+aKey"
              :disabled="areaActive"
              prepend-inner-icon="mdi-map-marker-radius-outline"
              append-icon="mdi-arrow-down-drop-circle-outline"
              :no-data-text="$t('common.noDataAvailable')">
            </v-select>
          </v-flex>
          <!-- Date range select -->
          <v-flex xs6 sm3 md2>
            <v-select class="ml-2"
              v-model="timePeriod"
              :items="timePeriodOptions"
              :label="$t('cleaningEfficiency.dateRange')"
              outlined
              prepend-inner-icon="mdi-calendar-range"
              append-icon="mdi-arrow-down-drop-circle-outline"
              @change="onTimePeriodChange">
            </v-select>
          </v-flex>
          <!-- Include Weekends -->
          <v-flex xs6 sm3 md1 class="d-flex align-center ml-2">
            <v-checkbox
              v-model="includeWeekends"
              :label="$t('cleaningEfficiency.includeWeekends')"
              color="#213A8F"
              dense hide-details
              @change="loadData">
            </v-checkbox>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- ── QUICK FILTERS (stile WashroomFrequencyV2) ── -->
    <v-card width="100%" flat class="mb-2">
      <v-card-text class="py-0">
        <hr style="border-top: solid 1px; color: #BFCCE4" />
        <v-layout align-center>
          <v-flex xs6 sm6 md2 class="mt-4 mb-4 mr-4 ml-4">
            <h2 style="font-weight: bold; color: #213A8F;">{{ $t('cleaningEfficiency.quickFilters') }}</h2>
          </v-flex>
          <v-flex v-for="qf in quickFilters" :key="qf.value" xs6 sm6 md2>
            <v-btn text @click="setQuickFilter(qf.value)" class="mt-4 mb-4 mr-2 ml-2">
              <h2 :style="'color: ' + (timePeriod === qf.value ? '#213A8F' : '#DAE0EC')">| {{ qf.label }} |</h2>
            </v-btn>
          </v-flex>
          <v-spacer></v-spacer>
          <v-btn text @click="clearFilter" class="mr-4">
            <span style="color: #213A8F;">{{ $t('cleaningEfficiency.clearFilter') }}</span>
            <v-icon color="#213A8F" right small>mdi-close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-text>
    </v-card>

    <hr style="border-top:solid 1px;color:#BFCCE4" />

    <!-- ── MAIN CONTENT ── -->
    <div v-show="visualizza_dati && !overlay">

      <!-- Period label -->
      <div class="d-flex align-center justify-space-between mt-4 mb-2">
        <div>
          <h2 class="ce-page-title">{{ $t('cleaningEfficiency.title') }}</h2>
          <span class="caption ce-muted">{{ periodLabel }}</span>
        </div>
      </div>

      <!-- ── SUMMARY KPI CARDS ── -->
      <v-row class="mb-4" align="stretch">
        <v-col cols="12" sm="6" md="3" style="display:flex">
          <v-card outlined class="ce-kpi-card pa-4" style="width:100%">
            <div class="ce-kpi-label">{{ $t('cleaningEfficiency.avgReactionTime') }}</div>
            <div class="ce-kpi-value">{{ avgReactionTimeDisplay }}</div>
            <div class="caption ce-muted">{{ $t('cleaningEfficiency.reactionTimeDesc') }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3" style="display:flex">
          <v-card outlined class="ce-kpi-card pa-4" style="width:100%">
            <div class="ce-kpi-label">{{ $t('cleaningEfficiency.avgDailyCleans') }}</div>
            <div class="ce-kpi-value">{{ avgDailyCleanings }}</div>
            <div class="caption ce-muted">{{ $t('cleaningEfficiency.cleaningsPerDay') }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3" style="display:flex">
          <v-card outlined class="ce-kpi-card pa-4" style="width:100%">
            <div class="ce-kpi-label">{{ $t('cleaningEfficiency.avgDailyAlerts') }}</div>
            <div class="ce-kpi-value">{{ avgDailyAlerts }}</div>
            <div class="caption ce-muted">{{ $t('cleaningEfficiency.alertsPerDay') }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3" style="display:flex">
          <v-card outlined class="ce-kpi-card pa-4" style="width:100%">
            <div class="ce-kpi-label">{{ $t('cleaningEfficiency.avgManagedTraffic') }}</div>
            <div class="ce-kpi-value">{{ avgManagedTraffic }} <span class="ce-kpi-unit">pax</span></div>
            <div class="caption ce-muted">{{ $t('cleaningEfficiency.managedTrafficDesc') }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── DEVICE OVERVIEW + CURRENT AREA STATUS ── -->
      <v-row class="mb-4" align="stretch">

        <!-- Device Overview -->
        <v-col cols="12" md="6" style="display:flex">
          <v-card outlined class="pa-4 ce-card" style="width:100%">
            <div class="d-flex align-center mb-2">
              <span class="ce-overline mr-2">{{ $t('cleaningEfficiency.deviceOverview').toUpperCase() }}</span>
              <v-chip x-small :color="statusChipColor" :text-color="statusChipTextColor" class="ce-badge">
                <v-icon x-small left>{{ isDeviceOnline ? 'mdi-circle' : 'mdi-circle-outline' }}</v-icon>
                {{ isDeviceOnline ? $t('cleaningEfficiency.online') : $t('cleaningEfficiency.offline') }}
              </v-chip>
            </div>
            <div class="ce-sensor-name mb-1">{{ deviceName }}</div>
            <div class="caption ce-muted mb-1" v-if="locationBreadcrumb">
              <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ locationBreadcrumb }}
            </div>
            <div class="caption ce-muted mb-3" v-if="cleaningTypeLabel">
              <v-icon x-small class="mr-1">mdi-broom</v-icon>
              {{ $t('cleaningEfficiency.cleaningType') }}: {{ cleaningTypeLabel }}
            </div>
            <v-row dense>
              <v-col cols="4">
                <v-card outlined class="ce-info-tile pa-2 text-center">
                  <v-icon small color="#213A8F">mdi-wifi</v-icon>
                  <div class="caption ce-muted mt-1">{{ $t('cleaningEfficiency.lastDataReceived') }}</div>
                  <div class="ce-info-value">{{ formattedLastDataReceived }}</div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card outlined class="ce-info-tile pa-2 text-center">
                  <v-icon small color="#213A8F">mdi-calendar-check-outline</v-icon>
                  <div class="caption ce-muted mt-1">{{ $t('cleaningEfficiency.lastCleaning') }}</div>
                  <div class="ce-info-value">{{ formattedLastCleaning }}</div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card outlined class="ce-info-tile pa-2 text-center">
                  <v-icon small color="#213A8F">mdi-access-point</v-icon>
                  <div class="caption ce-muted mt-1">{{ $t('cleaningEfficiency.managedSensors') }}</div>
                  <div class="ce-info-value">{{ managedSensorsCount }} <span style="font-size:10px">sensors</span></div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- Current Area Status -->
        <v-col cols="12" md="6" style="display:flex">
          <v-card outlined class="pa-4 ce-card" style="width:100%">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="ce-overline">{{ $t('cleaningEfficiency.currentAreaStatus').toUpperCase() }}</span>
              <div class="d-flex align-center" style="gap:6px">
                <v-chip v-if="isAreaRecentlyCleaned" x-small color="#E8F5E9" text-color="#2E7D32" class="ce-badge">
                  {{ $t('cleaningEfficiency.areaRecentlyCleaned') }}
                </v-chip>
                <v-chip x-small color="transparent" class="ce-badge" style="border:1px solid #BFCCE4">
                  <v-icon x-small left color="#213A8F">mdi-signal-variant</v-icon>
                  {{ $t('cleaningEfficiency.live') }}
                </v-chip>
              </div>
            </div>

            <!-- Entries count -->
            <div class="ce-big-value mb-1">{{ currentEntriesDisplay }}</div>
            <div class="caption ce-muted mb-4">{{ $t('cleaningEfficiency.entriesSinceLastCleaning') }}</div>

            <!-- Traffic bar -->
            <div class="ce-traffic-bar-wrap mb-1">
              <div class="ce-traffic-bar-track">
                <div class="ce-traffic-bar-fill" :style="{ width: trafficFillPct + '%' }"></div>
                <!-- Threshold marker -->
                <div class="ce-threshold-marker" :style="{ left: trafficThresholdMarkerPct + '%' }"></div>
              </div>
            </div>
            <div class="d-flex justify-space-between caption ce-muted mb-2">
              <span>0</span>
              <span>{{ $t('cleaningEfficiency.threshold') }} {{ maxAreaThreshold }}</span>
            </div>

            <!-- Legend -->
            <div class="d-flex" style="gap:16px">
              <div class="d-flex align-center" style="gap:4px">
                <div style="width:12px;height:12px;border-radius:2px;background:#009CAB"></div>
                <span class="caption ce-muted">{{ $t('cleaningEfficiency.trafficSinceLastCleaning') }}</span>
              </div>
              <div class="d-flex align-center" style="gap:4px">
                <div style="width:12px;height:12px;border-radius:2px;background:#dae0ec"></div>
                <span class="caption ce-muted">{{ $t('cleaningEfficiency.remainingRange') }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── AREA SENSORS TABLE + AVG DAILY ENTRIES CHART ── -->
      <v-row class="mb-4" align="stretch">

        <!-- Area Sensors table -->
        <v-col cols="12" md="6" style="display:flex">
          <v-card outlined class="pa-4 ce-card" style="width:100%">
            <div class="ce-overline mb-1">{{ $t('cleaningEfficiency.managedEntries').toUpperCase() }}</div>
            <div class="ce-sensor-name mb-1">{{ $t('cleaningEfficiency.areaSensors') }}</div>
            <div class="caption ce-muted mb-3">
              {{ sensors.length }} {{ $t('cleaningEfficiency.associatedSensors') }} &nbsp;·&nbsp;
              {{ currentEntriesDisplay }} {{ $t('cleaningEfficiency.entriesSinceLastCleaningLabel') }}
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="ce-table-head">{{ $t('cleaningEfficiency.type') }}</th>
                    <th class="ce-table-head">{{ $t('cleaningEfficiency.name') }}</th>
                    <th class="ce-table-head text-right">{{ $t('cleaningEfficiency.liveUsage') }}</th>
                    <th class="ce-table-head text-right">{{ $t('cleaningEfficiency.cleaningThreshold') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sensor in sensors" :key="sensor.id">
                    <td>
                      <v-icon x-small color="#4CAF50" class="mr-1">mdi-circle</v-icon>
                      {{ sensor.type }}
                    </td>
                    <td style="color:#213A8F;font-weight:500">{{ sensor.name }}</td>
                    <td class="text-right">{{ Math.round(sensor.liveUsage) }}</td>
                    <td class="text-right">{{ sensor.cleaningThreshold }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Avg Daily Entries chart (horizontal bars) -->
        <v-col cols="12" md="6" style="display:flex">
          <v-card outlined class="pa-4 ce-card" style="width:100%">
            <div class="ce-sensor-name mb-1">{{ $t('cleaningEfficiency.avgDailyEntries') }}</div>
            <div class="caption ce-muted mb-3">{{ $t('cleaningEfficiency.avgDailyEntriesDesc') }}</div>
            <apexchart
              v-if="avgEntriesSeries.length"
              type="bar"
              height="220"
              :options="avgEntriesChartOptions"
              :series="avgEntriesSeries">
            </apexchart>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── DAILY EFFICIENCY & TRAFFIC CHART ── -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card outlined class="pa-4 ce-card">
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class="ce-sensor-name">{{ $t('cleaningEfficiency.dailyEfficiencyChart') }}<span v-if="chartPeriodLabel"> ({{ chartPeriodLabel }})</span></div>
              </div>
              <!-- Legend -->
              <div class="d-flex align-center" style="gap:16px">
                <div class="d-flex align-center" style="gap:4px">
                  <div style="width:24px;height:3px;background:#213A8F;border-radius:2px"></div>
                  <span class="caption ce-muted">{{ $t('cleaningEfficiency.cumulativeTraffic') }}</span>
                </div>
                <div class="d-flex align-center" style="gap:4px">
                  <v-icon small color="#FF9800">mdi-triangle</v-icon>
                  <span class="caption ce-muted">{{ $t('cleaningEfficiency.alert') }}</span>
                </div>
                <div class="d-flex align-center" style="gap:4px">
                  <v-icon small color="#4CAF50">mdi-circle</v-icon>
                  <span class="caption ce-muted">{{ $t('cleaningEfficiency.rfidClean') }}</span>
                </div>
              </div>
            </div>
            <apexchart
              v-if="trafficSeries.length"
              :key="'traffic-' + trafficChartKey"
              type="area"
              height="300"
              :options="trafficChartOptions"
              :series="trafficSeries">
            </apexchart>
            <div v-else class="text-center caption ce-muted py-8">
              {{ $t('cleaningEfficiency.noDataAvailable') }}
            </div>
          </v-card>
        </v-col>
      </v-row>

    </div>

    <!-- ── PLACEHOLDER (no data yet) ── -->
    <v-card flat class="mt-8 text-center" v-show="!visualizza_dati && !overlay">
      <v-card-text>
        <v-icon size="64" color="#BFCCE4">mdi-chart-timeline-variant-shimmer</v-icon>
        <p class="mt-4 ce-muted">{{ $t('cleaningEfficiency.selectAreaToLoad') }}</p>
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script>
import axios from 'axios';
import { formatDateTimeDisplay, formatDateDisplay } from '@/utils/dateTimezone';

export default {
  name: 'CleaningEfficiencyView',

  data() {
    return {
      overlay: false,
      visualizza_dati: false,

      // Cascade filter state
      clients: [],
      buildings: [],
      floors: [],
      areas: [],
      client: '',
      building: '',
      floor: '',
      area: '',
      buildingActive: true,
      floorActive: true,
      areaActive: true,
      bKey: 0,
      fKey: 0,
      aKey: 0,

      // Time period & options
      timePeriod: 'last_30_days',
      includeWeekends: false,

      // API response
      apiData: null,

      // Chart key — incrementato quando cambia la timezone per forzare re-render
      trafficChartKey: 0,
    };
  },

  computed: {
    // ─── Time period options ───
    timePeriodOptions() {
      return [
        { text: this.$t('cleaningEfficiency.last4Hours'), value: 'last_4_hours' },
        { text: this.$t('cleaningEfficiency.lastDay'),    value: 'last_day' },
        { text: this.$t('cleaningEfficiency.lastWeek'),   value: 'last_7_days' },
        { text: this.$t('cleaningEfficiency.last2Weeks'), value: 'last_14_days' },
        { text: this.$t('cleaningEfficiency.last30Days'), value: 'last_30_days' },
      ];
    },

    quickFilters() {
      return [
        { label: '4 HOURS',  value: 'last_4_hours' },
        { label: '1 DAY',    value: 'last_day' },
        { label: '1 WEEK',   value: 'last_7_days' },
        { label: '2 WEEKS',  value: 'last_14_days' },
        { label: '30 DAYS',  value: 'last_30_days' },
      ];
    },

    // ─── Period label ───
    periodLabel() {
      const days = this.apiData?.summary?.period?.days || 30;
      const suffix = this.includeWeekends ? '' : ' (Mon-Fri)';
      return `${days}-Day Area Average${suffix}`;
    },

    // ─── Device info shortcuts ───
    deviceName()         { return this.apiData?.deviceInfo?.name || '–'; },
    cleaningTypeLabel()  { return this.apiData?.deviceInfo?.cleaningType || ''; },
    managedSensorsCount(){ return this.apiData?.deviceInfo?.managedSensorsCount ?? '–'; },
    isDeviceOnline()     { return this.apiData?.deviceInfo?.status === 'active'; },

    statusChipColor()     { return this.isDeviceOnline ? '#E8F5E9' : '#FFEBEE'; },
    statusChipTextColor() { return this.isDeviceOnline ? '#2E7D32' : '#C62828'; },

    locationBreadcrumb() {
      const loc = this.apiData?.location;
      if (!loc) return '';
      return [loc.building, loc.floor, loc.area].filter(Boolean).join(' · ');
    },

    formattedLastDataReceived() {
      const ts = this.apiData?.deviceInfo?.lastDataReceived;
      if (!ts) return '–';
      const tz = this.$store.state.timezone || 'Europe/Rome';
      return formatDateTimeDisplay(ts, tz) || '–';
    },

    formattedLastCleaning() {
      const ts = this.apiData?.deviceInfo?.lastCleaning;
      if (!ts) return '–';
      const tz = this.$store.state.timezone || 'Europe/Rome';
      return formatDateTimeDisplay(ts, tz) || '–';
    },

    isAreaRecentlyCleaned() {
      const ts = this.apiData?.deviceInfo?.lastCleaning;
      if (!ts) return false;
      const diff = (Date.now() - new Date(ts).getTime()) / 1000 / 60; // minutes
      return diff < 120; // last 2 hours
    },

    // ─── Current status ───
    sensors() { return this.apiData?.managedEntries?.sensors || []; },

    currentEntries() { return this.apiData?.currentStatus?.entriesSinceLastCleaning || 0; },

    currentEntriesDisplay() { return Math.round(this.currentEntries); },

    maxAreaThreshold() {
      if (!this.sensors.length) return 100;
      return Math.max(...this.sensors.map(s => s.cleaningThreshold || 0)) || 100;
    },

    trafficFillPct() {
      const pct = (this.currentEntries / this.maxAreaThreshold) * 100;
      return Math.min(Math.max(pct, 0), 100);
    },

    trafficThresholdMarkerPct() {
      return 100; // threshold marker at 100% = the maxAreaThreshold position
    },

    // ─── Summary KPI shortcuts ───
    summary() { return this.apiData?.summary || {}; },

    avgReactionTimeDisplay() {
      const min = this.summary.avgReactionTime;
      if (min == null || isNaN(min)) return '–';
      if (min >= 60) return (min / 60).toFixed(1) + ' h';
      return Math.round(min) + ' min';
    },

    avgDailyCleanings() {
      const v = this.summary.avgDailyCleans;
      return (v != null && !isNaN(v)) ? parseFloat(v.toFixed(1)) : '–';
    },

    avgDailyAlerts() {
      const v = this.summary.avgDailyAlerts;
      return (v != null && !isNaN(v)) ? parseFloat(v.toFixed(1)) : '–';
    },

    avgManagedTraffic() {
      const v = this.summary.avgManagedTraffic;
      return (v != null && !isNaN(v)) ? Math.round(v) : '–';
    },

    // ─── Chart series (computed per reattività automatica) ───
    trafficSeries() {
      const gd = this.apiData?.graphData;
      // Fallback su nomi di chiave alternativi restituiti dall'API
      const trafficData = gd?.trafficData
        || gd?.traffic
        || gd?.data
        || this.apiData?.trafficData
        || [];
      if (!trafficData.length) return [];
      return [{
        name: this.$t('cleaningEfficiency.cumulativeTraffic'),
        data: trafficData.map(d => ({
          x: new Date(d.timestamp || d.time || d.date).getTime(),
          y: Math.round((d.cumulativeCount ?? d.cumulative ?? d.value ?? d.count ?? 0) * 10) / 10,
        })).filter(pt => !isNaN(pt.x)),
      }];
    },

    avgEntriesSeries() {
      const sensors = this.sensors;
      if (!sensors.length) return [];
      return [{
        name: this.$t('cleaningEfficiency.avgDailyEntries'),
        data: sensors.map(s => Math.round(s.avgDailyEntries * 10) / 10),
      }];
    },

    // ─── Label periodo per il titolo del grafico traffico ───
    chartPeriodLabel() {
      const gd = this.apiData?.graphData;
      if (!gd) return '';
      const tz = this.$store.state.timezone || 'Europe/Rome';
      const start = formatDateDisplay(gd.startDate, tz);
      if (this.timePeriod === 'last_day' || this.timePeriod === 'last_4_hours') return start || '';
      const end = formatDateDisplay(gd.endDate, tz);
      return (start && end) ? `${start} - ${end}` : (start || '');
    },

    // ─── Avg Daily Entries chart options ───
    avgEntriesChartOptions() {
      const labels = this.sensors.map(s => s.name);
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
        colors: ['#213A8F'],
        xaxis: { categories: labels, labels: { style: { fontSize: '12px' } } },
        yaxis: { labels: { style: { fontSize: '12px' } } },
        grid: { borderColor: '#BFCCE4' },
        dataLabels: { enabled: true, style: { fontSize: '11px', colors: ['#fff'] } },
        tooltip: { y: { formatter: v => Math.round(v * 10) / 10 + ' entries/day' } },
      };
    },

    // ─── Traffic chart options ───
    trafficChartOptions() {
      const tz = this.$store.state.timezone || 'Europe/Rome';
      const isIntraDay = ['last_4_hours', 'last_day'].includes(this.timePeriod);
      const threshold = this.maxAreaThreshold;
      const gd = this.apiData?.graphData;
      const alerts    = gd?.alerts    || gd?.alertEvents    || [];
      const cleanings = gd?.cleanings || gd?.cleaningEvents || [];

      // Y-axis annotation (threshold line)
      const yaxisAnnotations = [{
        y: threshold,
        borderColor: '#FF9800',
        borderWidth: 2,
        strokeDashArray: 5,
        label: {
          text: this.$t('cleaningEfficiency.alertThreshold'),
          style: { background: 'transparent', color: '#FF9800', fontSize: '11px' },
          position: 'left',
          offsetX: 10,
        },
      }];

      // Point annotations
      const pointAnnotations = [
        ...alerts.map(a => ({
          x: new Date(a.timestamp).getTime(),
          y: a.count ?? 0,
          marker: { size: 8, fillColor: '#FF9800', strokeColor: '#FF9800', shape: 'triangle' },
          label: {
            text: 'Alert ' + this.formatTime(a.timestamp, tz),
            style: { background: '#FF9800', color: '#fff', fontSize: '10px', padding: { left: 5, right: 5, top: 2, bottom: 2 } },
            offsetY: -5,
          },
        })),
        ...cleanings.map(c => ({
          x: new Date(c.timestamp).getTime(),
          y: 0,
          marker: { size: 8, fillColor: '#4CAF50', strokeColor: '#4CAF50', shape: 'circle' },
          label: {
            text: 'Cleaned ' + this.formatTime(c.timestamp, tz),
            style: { background: '#4CAF50', color: '#fff', fontSize: '10px', padding: { left: 5, right: 5, top: 2, bottom: 2 } },
            offsetY: -8,
          },
        })),
      ];

      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'inherit',
          zoom: { enabled: false },
        },
        colors: ['#213A8F'],
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
        },
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        xaxis: {
          type: 'datetime',
          datetimeUTC: false,
          datetimeFormatter: isIntraDay
            ? { hour: 'HH:mm', minute: 'HH:mm' }
            : { year: 'yyyy', month: 'dd MMM', day: 'dd/MM', hour: 'HH:mm' },
          labels: { style: { fontSize: '11px', colors: '#8A98B5' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          title: { text: 'Entries', style: { fontSize: '12px', color: '#8A98B5' } },
          labels: { style: { fontSize: '11px', colors: '#8A98B5' } },
          min: 0,
        },
        grid: { borderColor: '#F0F3FA', strokeDashArray: 4 },
        annotations: {
          yaxis: yaxisAnnotations,
          points: pointAnnotations,
        },
        tooltip: {
          x: {
            formatter: (v) => {
              try {
                return new Date(v).toLocaleDateString('it-IT', {
                  timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric',
                }) + (isIntraDay ? ' ' + new Date(v).toLocaleTimeString('it-IT', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }) : '');
              } catch { return String(v); }
            },
          },
          y: { formatter: v => Math.round(v) + ' entries' },
        },
        legend: { show: false },
      };
    },
  },

  watch: {
    '$store.state.timezone'() {
      // Forza il re-render del grafico traffico quando cambia la timezone
      this.trafficChartKey++;
    },

    client(value) {
      if (this._initializingFromParams) return;
      if (!value) { this.resetBelowClient(); return; }
      this.buildings = [];
      this.building  = '';
      this.floors    = [];
      this.floor     = '';
      this.areas     = [];
      this.area      = '';
      this.buildingActive = true;
      this.floorActive    = true;
      this.areaActive     = true;
      this.bKey++;

      axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, { companyId: value })
        .then(res => {
          this.buildings = (res.data || []).map(el => ({ text: el.BuildingName, value: el.id }));
          this.buildingActive = this.buildings.length === 0;
          this.bKey++;
        })
        .catch(() => { this.buildingActive = true; this.bKey++; });
    },

    building(value) {
      if (this._initializingFromParams) return;
      if (!value) { this.resetBelowBuilding(); return; }
      this.floors  = [];
      this.floor   = '';
      this.areas   = [];
      this.area    = '';
      this.floorActive = true;
      this.areaActive  = true;
      this.fKey++;

      axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, { buildingId: value })
        .then(res => {
          this.floors = (res.data || []).map(el => ({ text: el.FloorName, value: el.id }));
          this.floorActive = this.floors.length === 0;
          this.fKey++;
        })
        .catch(() => { this.floorActive = true; this.fKey++; });
    },

    floor(value) {
      if (this._initializingFromParams) return;
      if (!value) { this.resetBelowFloor(); return; }
      this.areas  = [];
      this.area   = '';
      this.areaActive = true;
      this.aKey++;

      axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, { floorId: value })
        .then(res => {
          this.areas = (res.data || []).map(el => ({ text: el.AreaName, value: el.id }));
          this.areaActive = this.areas.length === 0;
          this.aKey++;
        })
        .catch(() => { this.areaActive = true; this.aKey++; });
    },

    area(value) {
      if (this._initializingFromParams) return;
      if (value) this.loadData();
    },
  },

  methods: {
    // ─── Cascade reset helpers ───
    resetBelowClient() {
      this.buildings = []; this.building = '';
      this.floors    = []; this.floor    = '';
      this.areas     = []; this.area     = '';
      this.buildingActive = true; this.floorActive = true; this.areaActive = true;
      this.bKey++; this.fKey++; this.aKey++;
    },
    resetBelowBuilding() {
      this.floors = []; this.floor = '';
      this.areas  = []; this.area  = '';
      this.floorActive = true; this.areaActive = true;
      this.fKey++; this.aKey++;
    },
    resetBelowFloor() {
      this.areas = []; this.area = '';
      this.areaActive = true;
      this.aKey++;
    },

    clearFilter() {
      this.client   = '';
      this.building = '';
      this.floor    = '';
      this.area     = '';
      this.resetBelowClient();
      this.visualizza_dati = false;
      this.apiData = null;
    },

    setQuickFilter(period) {
      this.timePeriod = period;
      if (this.area) this.loadData();
    },

    onTimePeriodChange() {
      if (this.area) this.loadData();
    },

    formatTime(timestamp, tz) {
      if (!timestamp) return '';
      try {
        const d = new Date(timestamp);
        return d.toLocaleTimeString('it-IT', {
          timeZone: tz,
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch { return ''; }
    },

    // ─── Resolve names from IDs (con fallback su override passati da ManageSensors) ───
    getBuildingName() {
      const found = this.buildings.find(b => b.value === this.building);
      if (found) return found.text;
      return this._overrideBuildingName || String(this.building);
    },
    getFloorName() {
      const found = this.floors.find(f => f.value === this.floor);
      if (found) return found.text;
      return this._overrideFloorName || String(this.floor);
    },
    getAreaName() {
      const found = this.areas.find(a => a.value === this.area);
      if (found) return found.text;
      return this._overrideAreaName || String(this.area);
    },

    // ─── Main data load ───
    async loadData() {
      if (!this.area) {
        console.warn('[CE loadData] area è vuota, skip');
        return;
      }

      this.overlay = true;
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) { console.warn('[CE loadData] No JWT token'); return; }

        const params = {
          timePeriod: this.timePeriod,
          building:   this.getBuildingName(),
          floor:      this.getFloorName(),
          area:       this.getAreaName(),
        };
        if (this.includeWeekends) params.includeWeekends = true;

        console.log('[CE loadData] chiamata API con params:', JSON.stringify(params));

        const response = await axios.get(
          this.$store.state.BASE_URL + '/v1/analytics/area-cleaning-efficiency',
          { params, headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('[CE loadData] response.data keys:', Object.keys(response.data || {}));
        console.log('[CE loadData] graphData:', response.data?.graphData);
        console.log('[CE loadData] trafficData:', response.data?.graphData?.trafficData);

        this.apiData = response.data;
        this.visualizza_dati = true;
        // Aspetta che il DOM sia aggiornato (v-show attivo) prima di forzare
        // il re-mount del grafico: ApexCharts ha bisogno del container visibile
        // per calcolare le dimensioni correttamente
        await this.$nextTick();
        this.trafficChartKey++;
      } catch (error) {
        console.error('Errore nel caricamento dati cleaning efficiency:', error);
        this.visualizza_dati = false;
      } finally {
        this.overlay = false;
      }
    },

    // ─── Initialise clients list and handle route params ───
    async init() {
      this.overlay = true;
      try {
        // Legge i parametri passati dalla navigazione (ManageSensorsView)
        const p = this.$route.params || {};
        console.log('[CE init] route params:', JSON.stringify(p));

        const { company, building, buildingName, floor, floorName, area, areaName } = p;

        // Carica la lista clienti (necessaria per il dropdown)
        const clientsResponse = await axios.post(this.$store.state.URL_GET_AVAILABLE_CLIENT)
          .catch(e => { console.warn('[CE init] clients error:', e); return { data: [] }; });
        this.clients = (clientsResponse.data || []).map(el => ({
          text: el.CompanyName,
          value: el.id,
        }));

        if (company && building && floor && area) {
          // Blocca i watcher per evitare race condition (il watcher `building`
          // cancellerebbe floors/areas già caricati)
          this._initializingFromParams = true;

          this.buildingActive = false;
          this.floorActive    = false;
          this.areaActive     = false;

          // Carica in parallelo tutte le liste della cascata
          const [bRes, fRes, aRes] = await Promise.all([
            axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, { companyId: company })
              .catch(e => { console.warn('[CE init] buildings error:', e); return { data: [] }; }),
            axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, { buildingId: building })
              .catch(e => { console.warn('[CE init] floors error:', e); return { data: [] }; }),
            axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, { floorId: floor })
              .catch(e => { console.warn('[CE init] areas error:', e); return { data: [] }; }),
          ]);

          this.buildings = (bRes.data || []).map(el => ({ text: el.BuildingName, value: el.id }));
          this.floors    = (fRes.data || []).map(el => ({ text: el.FloorName,    value: el.id }));
          this.areas     = (aRes.data || []).map(el => ({ text: el.AreaName,     value: el.id }));

          // Fallback: se le cascade non restituiscono dati, usa i nomi passati come params
          if (!this.buildings.length && buildingName)
            this.buildings = [{ text: buildingName, value: building }];
          if (!this.floors.length && floorName)
            this.floors = [{ text: floorName, value: floor }];
          if (!this.areas.length && areaName)
            this.areas = [{ text: areaName, value: area }];

          console.log('[CE init] buildings:', this.buildings);
          console.log('[CE init] floors:', this.floors);
          console.log('[CE init] areas:', this.areas);

          // Imposta i valori senza innescare i watcher
          this.client   = company;
          this.building = building;
          this.floor    = floor;
          this.area     = area;

          this._initializingFromParams = false;

          // Sovrascrivi i nomi risolvibili dai dropdown: se la cascata è vuota e abbiamo
          // i nomi dai params, li salviamo come override per loadData()
          this._overrideBuildingName = buildingName || null;
          this._overrideFloorName    = floorName    || null;
          this._overrideAreaName     = areaName     || null;

          await this.loadData();
          return;
        }
      } catch (err) {
        console.error('[CE init] Errore:', err);
      } finally {
        this.overlay = false;
      }
    },
  },

  async mounted() {
    await this.init();
  },
};
</script>

<style scoped>

/* ── Filter bar ── */
#ceFilterBar .v-input--dense .v-input__slot { min-height: 40px; }

/* ── Typography ── */
.ce-page-title  { font-size: 22px; font-weight: 700; color: #1A2B5A; }
.ce-overline    { font-size: 10px; font-weight: 600; letter-spacing: 1.2px; color: #8A98B5; }
.ce-sensor-name { font-size: 20px; font-weight: 700; color: #1A2B5A; }
.ce-muted       { color: #8A98B5 !important; }
.ce-big-value   { font-size: 40px; font-weight: 700; color: #1A2B5A; line-height: 1.1; }

/* ── Cards ── */
.ce-card {
  border-radius: 10px !important;
  border-color: #E8EDF5 !important;
}

/* ── Summary KPI cards ── */
.ce-kpi-card {
  border-radius: 10px !important;
  border-color: #E8EDF5 !important;
}
.ce-kpi-label { font-size: 11px; font-weight: 600; letter-spacing: 0.8px; color: #8A98B5; text-transform: uppercase; margin-bottom: 4px; }
.ce-kpi-value { font-size: 28px; font-weight: 700; color: #1A2B5A; line-height: 1.1; margin-bottom: 2px; }
.ce-kpi-unit  { font-size: 14px; font-weight: 400; color: #8A98B5; }

/* ── Badge chip ── */
.ce-badge { font-size: 10px !important; font-weight: 600; height: 20px !important; border-radius: 10px !important; }

/* ── Info tiles (inside overview card) ── */
.ce-info-tile   { border-radius: 8px !important; border-color: #E8EDF5 !important; min-height: 64px; }
.ce-info-value  { font-size: 12px; font-weight: 600; color: #1A2B5A; word-break: break-word; margin-top: 2px; }

/* ── Traffic bar ── */
.ce-traffic-bar-wrap  { position: relative; }
.ce-traffic-bar-track {
  height: 18px;
  background: #dae0ec;
  border-radius: 9px;
  overflow: hidden;
  position: relative;
}
.ce-traffic-bar-fill  {
  height: 100%;
  background: linear-gradient(90deg, #009CAB 0%, #213A8F 100%);
  border-radius: 9px;
  transition: width 0.4s ease;
}
.ce-threshold-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255,255,255,0.8);
  transform: translateX(-50%);
}

/* ── Table ── */
.ce-table-head { font-size: 11px !important; font-weight: 600 !important; color: #213A8F !important; text-transform: uppercase; letter-spacing: 0.5px; }

</style>
